import React from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Grid from "@mui/material/Grid"
import Toggle from "component/toggle/Toggle"
import DatePicker from "component/datePicker/datePicker"
import IconsColor from "util/iconsColor"
import Checkbox from "component/checkbox/checkbox"
import styles from "../../asset/styles/excursionDetail/excursionDetail.module.scss"
import Button from "component/button/button"
import {
    ExcursionDetailModel,
    BlackoutDateModel,
    ExcursionUnavailableDayModel,
} from "../../models"

const WEEK_DAYS = [
    { id: 1, name: "Mon", label: "Monday" },
    { id: 2, name: "Tue", label: "Tuesday" },
    { id: 3, name: "Wed", label: "Wednesday" },
    { id: 4, name: "Thu", label: "Thursday" },
    { id: 5, name: "Fri", label: "Friday" },
    { id: 6, name: "Sat", label: "Saturday" },
    { id: 7, name: "Sun", label: "Sunday" },
]

/** API date is ISO; DatePicker expects YYYY-MM-DD */
const isoToDateValue = (iso: string): string => (iso ? iso.slice(0, 10) : "")

/** DatePicker returns YYYY-MM-DD; store as ISO for API */
const dateValueToIso = (value: string): string =>
    value ? `${value}T00:00:00.000Z` : ""

/** Returns indices of blackout date rows where start > end (invalid range) */
function getInvalidRangeIndices(dates: BlackoutDateModel[]): Set<number> {
    const indices = new Set<number>()
    for (let i = 0; i < dates.length; i++) {
        const row = dates[i]
        if ((row.start_date ?? "").trim() === "" || (row.end_date ?? "").trim() === "") continue
        const startT = new Date(row.start_date).getTime()
        const endT = new Date(row.end_date).getTime()
        if (isNaN(startT) || isNaN(endT)) continue
        if (startT > endT) indices.add(i)
    }
    return indices
}

/** Returns indices of blackout date rows that are part of an overlapping pair */
function getOverlappingBlackoutIndices(dates: BlackoutDateModel[]): Set<number> {
    const indices = new Set<number>()
    for (let i = 0; i < dates.length; i++) {
        const a = dates[i]
        if ((a.start_date ?? "").trim() === "" || (a.end_date ?? "").trim() === "") continue
        const startA = new Date(a.start_date).getTime()
        const endA = new Date(a.end_date).getTime()
        if (isNaN(startA) || isNaN(endA) || endA < startA) continue
        for (let j = i + 1; j < dates.length; j++) {
            const b = dates[j]
            if ((b.start_date ?? "").trim() === "" || (b.end_date ?? "").trim() === "") continue
            const startB = new Date(b.start_date).getTime()
            const endB = new Date(b.end_date).getTime()
            if (isNaN(startB) || isNaN(endB) || endB < startB) continue
            if (startA <= endB && startB <= endA) {
                indices.add(i)
                indices.add(j)
            }
        }
    }
    return indices
}

interface ExcursionBlackOutDateProps {
    excursion: ExcursionDetailModel
    onChange: (updates: Partial<ExcursionDetailModel>) => void
    errors?: { blackout?: string; blackout_overlap?: string; unavailable_days?: string }
}

const ExcursionBlackOutDate: React.FC<ExcursionBlackOutDateProps> = ({
    excursion,
    onChange,
    errors = {},
}) => {
    const enabled = excursion.flag_enable_blackout_dates
    const blackoutDates = React.useMemo(
        () => excursion.blackout_dates ?? [],
        [excursion.blackout_dates]
    )
    const unavailableDays = excursion.excursion_unavailable_days ?? []
    const flagUnavailableDays = excursion.flag_excursion_not_available_on_days

    const updateBlackoutDates = (next: BlackoutDateModel[]) => {
        onChange({ blackout_dates: next })
    }

    const updateUnavailableDays = (next: ExcursionUnavailableDayModel[]) => {
        onChange({ excursion_unavailable_days: next })
    }

    const handleToggleEnable = (checked: boolean) => {
        onChange({ flag_enable_blackout_dates: checked })
        // When enabling, add one default row with empty dates if none exist
        if (checked && blackoutDates.length === 0) {
            const newEntry: BlackoutDateModel = {
                blackout_dates_id: crypto.randomUUID(),
                excursion_id: excursion.excursion_id,
                start_date: "",
                end_date: "",
            }
            updateBlackoutDates([newEntry])
        }
    }

    const handleBlackoutStartChange = (index: number, value: string) => {
        const next = [...blackoutDates]
        const range = next[index]
        const currentEnd = (range.end_date ?? "").trim()
        const newStartIso = dateValueToIso(value)
        // If end date is set and new start is after end, clear end date
        if (currentEnd && value) {
            const startT = new Date(newStartIso).getTime()
            const endT = new Date(currentEnd).getTime()
            if (!isNaN(startT) && !isNaN(endT) && startT > endT) {
                next[index] = { ...range, start_date: newStartIso, end_date: "" }
                updateBlackoutDates(next)
                return
            }
        }
        next[index] = { ...range, start_date: newStartIso }
        updateBlackoutDates(next)
    }

    const handleBlackoutEndChange = (index: number, value: string) => {
        const next = [...blackoutDates]
        next[index] = { ...next[index], end_date: dateValueToIso(value) }
        updateBlackoutDates(next)
    }

    const handleDeleteBlackout = (index: number) => {
        const next = blackoutDates.filter((_, i) => i !== index)
        updateBlackoutDates(next)
    }

    const handleAddDate = () => {
        const newEntry: BlackoutDateModel = {
            blackout_dates_id: crypto.randomUUID(),
            excursion_id: excursion.excursion_id,
            start_date: "",
            end_date: "",
        }
        updateBlackoutDates([...blackoutDates, newEntry])
    }

    const handleUnavailableYesNo = (yes: boolean) => {
        onChange({ flag_excursion_not_available_on_days: yes })
    }

    const handleWeekDayToggle = (dayId: number, dayName: string) => {
        const existing = unavailableDays.find((d) => d.week_day_id === dayId)
        if (existing) {
            updateUnavailableDays(unavailableDays.filter((d) => d.week_day_id !== dayId))
        } else {
            updateUnavailableDays([
                ...unavailableDays,
                {
                    excursion_unavailable_days_id: crypto.randomUUID(),
                    excursion_id: excursion.excursion_id,
                    week_day_id: dayId,
                    week_day_name: dayName,
                },
            ])
        }
    }

    const isDaySelected = (dayId: number) =>
        unavailableDays.some((d) => d.week_day_id === dayId)

    const overlappingIndices = React.useMemo(
        () => (errors.blackout_overlap ? getOverlappingBlackoutIndices(blackoutDates) : new Set<number>()),
        [blackoutDates, errors.blackout_overlap]
    )

    const invalidRangeIndices = React.useMemo(
        () => getInvalidRangeIndices(blackoutDates),
        [blackoutDates]
    )

    const INVALID_RANGE_MESSAGE = "Start date must be before or equal to end date"

    return (
        <div className={utilStyles.pageContainerContent}>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Toggle
                        rightLabel="Enable Blackout Dates"
                        checked={enabled}
                        onChange={handleToggleEnable}
                    />
                </Grid>

                {enabled && (
                    <>
                        {errors.blackout && (
                            <Grid size={{ xs: 12 }}>
                                <span style={{ color: "#d32f2f", fontSize: 14 }}>{errors.blackout}</span>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 8 }}>
                            <Grid container spacing={2}>
                                {blackoutDates.map((range, index) => (
                                    <Grid
                                        key={range.blackout_dates_id}
                                        size={{ xs: 12 }}
                                    >
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid size={{ xs: 12, sm: 5, md: 5, lg: 4, xl: 5 }}>
                                                <DatePicker
                                                    label="Start Date"
                                                    value={isoToDateValue(range.start_date)}
                                                    onChange={(value) =>
                                                        handleBlackoutStartChange(index, value)
                                                    }
                                                    placeholder="Start date"
                                                    maxDate={
                                                        range.end_date
                                                            ? isoToDateValue(range.end_date)
                                                            : undefined
                                                    }
                                                    error={
                                                        invalidRangeIndices.has(index)
                                                            ? INVALID_RANGE_MESSAGE
                                                            : overlappingIndices.has(index)
                                                              ? errors.blackout_overlap
                                                              : undefined
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 5, md: 5, lg: 4, xl: 5 }}>
                                                <DatePicker
                                                    label="End Date"
                                                    value={isoToDateValue(range.end_date)}
                                                    onChange={(value) =>
                                                        handleBlackoutEndChange(index, value)
                                                    }
                                                    placeholder="End date"
                                                    minDate={
                                                        range.start_date
                                                            ? isoToDateValue(range.start_date)
                                                            : undefined
                                                    }
                                                    error={
                                                        invalidRangeIndices.has(index)
                                                            ? INVALID_RANGE_MESSAGE
                                                            : overlappingIndices.has(index)
                                                              ? errors.blackout_overlap
                                                              : undefined
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 2, md: 2, lg: 4, xl: 2 }}>
                                                <div className={styles.dateActionButtons}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteBlackout(index)}
                                                        aria-label="Delete date range"
                                                        style={{
                                                            background: "none",
                                                            border: "none",
                                                            padding: 0,
                                                            cursor: "pointer",
                                                            display: "inline-flex",
                                                            color: "#d32f2f",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <IconsColor
                                                            iconName="deleteIcon"
                                                            width="24px"
                                                            height="24px"
                                                            fillColor="none"
                                                            strokeColor="currentColor"
                                                        />
                                                    </button>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                            <button
                                type="button"
                                className={utilStyles.addBtn}
                                onClick={handleAddDate}
                            >
                                + Add Date
                            </button>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <div className={styles.unavailableDaysSection}>
                                <span>Are there Any Days When This Excursion Is Unavailable?</span>
                                <Checkbox
                                    label="Yes"
                                    checked={flagUnavailableDays}
                                    onChange={(val) => handleUnavailableYesNo(val)}
                                />
                                <Checkbox
                                    label="No"
                                    checked={!flagUnavailableDays}
                                    onChange={(val) => handleUnavailableYesNo(!val)}
                                />
                            </div>
                        </Grid>

                        {flagUnavailableDays && (
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <span>Select The Days When This Excursion Is Unavailable</span>
                                <br />
                                <div className={styles.weekDayButtons}>
                                    {WEEK_DAYS.map((day) => (
                                        <span
                                            key={day.id}
                                            className={
                                                isDaySelected(day.id)
                                                    ? styles.weekDayBtnContained
                                                    : styles.weekDayBtnOutlined
                                            }
                                        >
                                            <Button
                                                style={{
                                                    margin: "16px 14px 0 0",
                                                    padding: "5px 10px",
                                                    minWidth: "70px",
                                                }}
                                                type={isDaySelected(day.id) ? "contained" : "outlined"}
                                                label={day.name}
                                                onClick={() =>
                                                    handleWeekDayToggle(day.id, day.label)
                                                }
                                            />
                                        </span>
                                    ))}
                                </div>
                                {errors.unavailable_days && (
                                    <div style={{ marginTop: 8 }}>
                                        <span style={{ color: "#d32f2f", fontSize: 14 }}>
                                            {errors.unavailable_days}
                                        </span>
                                    </div>
                                )}
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </div>
    )
}

export default ExcursionBlackOutDate
